'use client';
import type { IBaseTagPayload } from './types/base';
import { EVENT_NAMES } from './types/base';

// IMPORTANT: This module requires window.adobeDataLayer to be pre-initialized
// before any tagging calls are made. This is handled by the beforeInteractive
// inline script in @kmx/third-party-scripts — ThirdPartyScripts.tsx must be
// rendered in the app layout for events to be pushed correctly.

interface IAdobeDataLayer {
  push: (event: object) => void;
}

declare global {
  interface Window {
    adobeDataLayer: IAdobeDataLayer;
  }
}

const tryPush = (event: IBaseTagPayload): void => {
  if (!window.adobeDataLayer) {
    if (process.env.NODE_ENV !== 'development') return;

    console.warn('[tagging] window.adobeDataLayer is not defined.');
  }

  window.adobeDataLayer.push(event);
};

/**
 * Events received before pageLoad are held here and flushed once pageLoad
 * fires, ensuring pageLoad is always the first item in the data layer.
 * Set to null once pageLoad has been pushed.
 */
let eventQueue: IBaseTagPayload[] | null = [];
let pageLoadTimeoutId: ReturnType<typeof setTimeout> | null = null;

const clearPageLoadTimeout = (): void => {
  if (pageLoadTimeoutId === null) return;

  clearTimeout(pageLoadTimeoutId);
  pageLoadTimeoutId = null;
};

/** @internal — exposed for test isolation only */
export const resetQueue = (): void => {
  clearPageLoadTimeout();
  eventQueue = [];
};

const closeQueue = (): void => {
  eventQueue = null;
};

const flushQueue = (): void => {
  if (!eventQueue) return;
  for (const event of eventQueue) {
    tryPush(event);
  }
};

const finalizeQueue = (): void => {
  clearPageLoadTimeout();
  flushQueue();
  closeQueue();
};

const enqueueEvent = (event: IBaseTagPayload): void => {
  if (eventQueue?.length === 0) {
    pageLoadTimeoutId = setTimeout(closeQueue, 10_000);
  }

  eventQueue?.push(event);
};

export const pushToAdobeDataLayer = (event: IBaseTagPayload): void => {
  if (eventQueue === null) {
    tryPush(event);
    return;
  }

  if (event.event === EVENT_NAMES.pageLoad) {
    tryPush(event);
    finalizeQueue();
    return;
  }

  enqueueEvent(event);
};
